import { ApiError } from "../../../services/error-handler";
import { getHandler, postHandler, UnauthorisedError } from "../../../services/request-handler";
import { setAuthToken } from "../../../services/token-storage";

interface IPhoneOtpRequest {
  phoneOtpRequestId: string;
}

/**
 * Request an OTP code via phone
 * @param phone Phone number to send OTP to
 * @param captcha Captcha challenge value
 */
export async function requestPhoneOtp(phone: string, captcha: string): Promise<string> {
  const body = JSON.stringify({ 
    phone,
    captcha,
  });

  const response = await postHandler<IPhoneOtpRequest>(
    "/login/phone-otp/request",
    body,
  );

  if (response.error) {
    throw new ApiError(response.error);
  }

  return response.result.phoneOtpRequestId;
}

interface IUser {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  referralCode?: string;
  referralReward?: number;
  referralDeduction?: number;
}

export interface ILoginUser extends IUser {
  urn: string;
}

/**
 * Validate a phone OTP code
 * @param phoneOtpRequestId The OTP request ID
 * @param code The code snet to the phone
 */
export async function validatePhoneOtp(
  phoneOtpRequestId: string,
  code: string,
): Promise<ILoginUser> {
  const body = JSON.stringify({ phoneOtpRequestId, code });

  try {
    const response = await postHandler<ILoginUser>(
      "/login/phone-otp/validate",
      body,
    );

    if (response.error) {
      throw new ApiError(response.error);
    }

    const token = response.headers.get("token");

    if (token) {
      setAuthToken(token);
    }

    return response.result;
  } catch (err) {
    if (err instanceof UnauthorisedError) {
      throw new ApiError({ code: err.code, message: err.message });
    }

    throw err;
  }
}

interface IEmailOtpRequest {
  emailOtpRequestId: string;
}

/**
 * Request an OTP code via email
 * @param email Email address to send OTP to
 * @param captcha Captcha challenge value
 */
export async function requestEmailOtp(email: string, captcha: string): Promise<string> {
  const body = JSON.stringify({ 
    email,
    captcha,
  });

  const response = await postHandler<IEmailOtpRequest>(
    "/login/email-otp/request",
    body,
  );

  if (response.error) {
    throw new ApiError(response.error);
  }

  return response.result.emailOtpRequestId;
}

/**
 * Validate email OTP code
 * @param emailOtpRequestId The OTP request ID
 * @param code The code snet to the email
 */
export async function validateEmailOtp(
  emailOtpRequestId: string,
  code: string,
): Promise<ILoginUser> {
  const body = JSON.stringify({ emailOtpRequestId, code });

  try {
    const response = await postHandler<ILoginUser>(
      "/login/email-otp/validate",
      body,
    );

    if (response.error) {
      throw new ApiError(response.error);
    }

    const token = response.headers.get("token");

    if (token) {
      setAuthToken(token);
    }

    return response.result;
  } catch (err) {
    if (err instanceof UnauthorisedError) {
      throw new ApiError({ code: err.code, message: err.message });
    }

    throw err;
  }
}

export type IApiSignupOtpType = "email" | "phone";

interface IApiSignupUser {
  email: string;
  firstName: string;
  lastName: string;
  otpCode: string;
  otpRequestId: string;
  otpType: IApiSignupOtpType;
  password?: string;
  phone: string;
  city: string;
  agreeMailListSignup: boolean;
}

/**
 * Sign up a new customer
 * @param data Customer details
 */
export async function signupCustomer(
  data: IApiSignupUser,
): Promise<ILoginUser> {
  const body = JSON.stringify(data);

  const response = await postHandler<ILoginUser>("/signup", body, false);

  if (response.error) {
    throw new ApiError(response.error);
  }

  const token = response.headers.get("token");

  if (token) {
    setAuthToken(token);
  }

  return response.result;
}

/**
 * Get all available cities
 */
export async function getCities(): Promise<string[]> {
  const response = await getHandler("/customer/cities", false);

  if (response.error) {
    throw new ApiError(response.error);
  }

  return response.result as string[];
}
